import React, { useState, useEffect } from 'react';
import { superAdminApiService } from '../services/SuperAdminApiService';
import { Settings, CloudSun, Mail, Save, FlaskConical } from 'lucide-react';
import './SuperAdminConfigurazioni.css';

interface ConfigurazioneMeteo {
  OPENWEATHER_API_KEY: string;
  meteo_aggiornamento_automatico: string;
  meteo_intervallo_ore: string;
  meteo_provider: string;
}

interface ConfigurazioneEmail {
  BREVO_API_KEY: string;
  email_mittente: string;
  email_mittente_nome: string;
}

const SuperAdminConfigurazioni: React.FC = () => {
  const [configMeteo, setConfigMeteo] = useState<ConfigurazioneMeteo>({
    OPENWEATHER_API_KEY: '',
    meteo_aggiornamento_automatico: 'true',
    meteo_intervallo_ore: '3',
    meteo_provider: 'openweathermap'
  });

  const [configEmail, setConfigEmail] = useState<ConfigurazioneEmail>({
    BREVO_API_KEY: '',
    email_mittente: '',
    email_mittente_nome: ''
  });

  const [selectedTenant, setSelectedTenant] = useState<number | 'global'>('global');
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'meteo' | 'email'>('meteo');

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      loadConfigurations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch solo al cambio tenant/tab; loadConfigurations è ricreata ad ogni render
  }, [selectedTenant, activeTab]);

  const loadTenants = async () => {
    try {
      const data = await superAdminApiService.getAllTenants();
      setTenants(data);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const loadConfigurations = async () => {
    setLoading(true);
    try {
      const baseURL = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/superadmin'
        : '/api/superadmin';
      
      if (activeTab === 'meteo') {
        // Carica configurazioni meteo
        const response = await fetch(`${baseURL}/configurations/weather/${selectedTenant}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setConfigMeteo(data.configurations || configMeteo);
        }
      } else {
        // Carica configurazioni email
        const response = await fetch(`${baseURL}/configurations/email/${selectedTenant}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setConfigEmail(data.configurations || configEmail);
        }
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const saveConfigurations = async () => {
    setSaving(true);
    try {
      const baseURL = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/superadmin'
        : '/api/superadmin';
      
      const endpoint = activeTab === 'meteo' ? 'weather' : 'email';
      const data = activeTab === 'meteo' ? configMeteo : configEmail;
      
      const response = await fetch(`${baseURL}/configurations/${endpoint}/${selectedTenant}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Configurazioni salvate' });
      } else {
        throw new Error('Errore nel salvataggio');
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const testMeteoConnection = async () => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/superadmin'
        : '/api/superadmin';
      
      const response = await fetch(`${baseURL}/configurations/weather/test/${selectedTenant}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: 'Test connessione meteo riuscito' });
      } else {
        setMessage({ type: 'error', text: `Test fallito: ${result.message}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const testEmailConnection = async () => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/superadmin'
        : '/api/superadmin';
      
      const response = await fetch(`${baseURL}/configurations/email/test/${selectedTenant}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('superadmin_token')}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: 'Test connessione email riuscito' });
      } else {
        setMessage({ type: 'error', text: `Test fallito: ${result.message}` });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  return (
    <div className="superadmin-configurazioni">
      <div className="configurazioni-header">
        <h1>
          <Settings size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Configurazioni API
        </h1>
        <p>Gestione delle configurazioni sensibili del sistema</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="configurazioni-controls">
        <div className="tenant-selector">
          <label>Tenant:</label>
          <select 
            value={selectedTenant} 
            onChange={(e) => setSelectedTenant(e.target.value === 'global' ? 'global' : parseInt(e.target.value))}
          >
            <option value="global">Configurazione Globale</option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                #{tenant.id} - {tenant.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="tab-selector">
          <button 
            className={activeTab === 'meteo' ? 'active' : ''}
            onClick={() => setActiveTab('meteo')}
          >
            <CloudSun size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            Meteo API
          </button>
          <button 
            className={activeTab === 'email' ? 'active' : ''}
            onClick={() => setActiveTab('email')}
          >
            <Mail size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            Email API
          </button>
        </div>
      </div>

      <div className="configurazioni-content">
        {loading ? (
          <div className="loading">Caricamento configurazioni...</div>
        ) : (
          <div className="config-sections">
            {activeTab === 'meteo' && (
              <div className="config-section">
                <h2>
                  <CloudSun size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                  Configurazione API Meteo
                </h2>
                
                <div className="form-group">
                  <label>OpenWeatherMap API Key</label>
                  <input
                    type="password"
                    value={configMeteo.OPENWEATHER_API_KEY}
                    onChange={(e) => setConfigMeteo(prev => ({
                      ...prev,
                      OPENWEATHER_API_KEY: e.target.value
                    }))}
                    placeholder="Inserisci la API Key di OpenWeatherMap"
                  />
                  <small>Ottieni la tua chiave gratuita su <a href="https://openweathermap.org/api" target="_blank" rel="noopener noreferrer">openweathermap.org</a></small>
                </div>

                <div className="form-group">
                  <label>Aggiornamento Automatico</label>
                  <select
                    value={configMeteo.meteo_aggiornamento_automatico}
                    onChange={(e) => setConfigMeteo(prev => ({
                      ...prev,
                      meteo_aggiornamento_automatico: e.target.value
                    }))}
                  >
                    <option value="true">Attivo</option>
                    <option value="false">Disattivo</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Intervallo Aggiornamento (ore)</label>
                  <select
                    value={configMeteo.meteo_intervallo_ore}
                    onChange={(e) => setConfigMeteo(prev => ({
                      ...prev,
                      meteo_intervallo_ore: e.target.value
                    }))}
                  >
                    <option value="1">1 ora</option>
                    <option value="3">3 ore</option>
                    <option value="6">6 ore</option>
                    <option value="12">12 ore</option>
                    <option value="24">24 ore</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Provider</label>
                  <input
                    type="text"
                    value={configMeteo.meteo_provider}
                    disabled
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                  />
                  <small>Attualmente supportato solo OpenWeatherMap</small>
                </div>

                <div className="form-actions">
                  <button 
                    onClick={testMeteoConnection}
                    className="btn btn-secondary"
                    disabled={!configMeteo.OPENWEATHER_API_KEY}
                  >
                    <FlaskConical size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                    Test Connessione
                  </button>
                  <button 
                    onClick={saveConfigurations}
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    <Save size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                    {saving ? 'Salvataggio...' : 'Salva Configurazione'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'email' && (
              <div className="config-section">
                <h2>
                  <Mail size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                  Configurazione API Email
                </h2>
                
                <div className="form-group">
                  <label>Brevo API Key</label>
                  <input
                    type="password"
                    value={configEmail.BREVO_API_KEY}
                    onChange={(e) => setConfigEmail(prev => ({
                      ...prev,
                      BREVO_API_KEY: e.target.value
                    }))}
                    placeholder="Inserisci la API Key di Brevo"
                  />
                  <small>Ottieni la tua chiave dall'account Brevo: Impostazioni &gt; SMTP &amp; API</small>
                </div>

                <div className="form-group">
                  <label>Email Mittente</label>
                  <input
                    type="email"
                    value={configEmail.email_mittente}
                    onChange={(e) => setConfigEmail(prev => ({
                      ...prev,
                      email_mittente: e.target.value
                    }))}
                    placeholder="mittente@esempio.it"
                  />
                  <small>L'email deve essere verificata nel tuo account Brevo</small>
                </div>

                <div className="form-group">
                  <label>Nome Mittente</label>
                  <input
                    type="text"
                    value={configEmail.email_mittente_nome}
                    onChange={(e) => setConfigEmail(prev => ({
                      ...prev,
                      email_mittente_nome: e.target.value
                    }))}
                    placeholder="Nome della Gelateria"
                  />
                  <small>Il nome che apparirà come mittente delle email</small>
                </div>

                <div className="form-actions">
                  <button 
                    onClick={testEmailConnection}
                    className="btn btn-secondary"
                    disabled={!configEmail.BREVO_API_KEY || !configEmail.email_mittente}
                  >
                    <FlaskConical size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                    Test Connessione
                  </button>
                  <button 
                    onClick={saveConfigurations}
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    <Save size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                    {saving ? 'Salvataggio...' : 'Salva Configurazione'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="configurazioni-info">
        <h3>Informazioni Importanti</h3>
        <div className="info-cards">
          <div className="info-card">
            <h4>Configurazioni Globali vs Tenant</h4>
            <p>Le configurazioni globali si applicano a tutti i tenant che non hanno configurazioni specifiche.</p>
          </div>
          <div className="info-card">
            <h4>Sicurezza</h4>
            <p>Queste API key sono sensibili e devono essere gestite solo da super amministratori.</p>
          </div>
          <div className="info-card">
            <h4>Funzioni Utente</h4>
            <p>Gli utenti normali possono ancora gestire destinatari email e fare test dall'interfaccia tenant.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminConfigurazioni;
